import { useMemo, useState } from 'react';
import { Box, Button, Chip, Collapse, Typography } from '@mui/material';
import { computeLineDiff } from '../../../utils/templateDiff';
import {
  CK, FONT_BODY, statusChipSx, insetPanelSx, sectionHeadingSx, emptyTextSx,
} from '../portalStyles';

// Diff line colours come from the CK palette (pastel fill + saturated text) so
// the diff body reads as part of the portal rather than a GitHub transplant.
const diffRowSx = {
  add: { bgcolor: '#F0F9F4', color: CK.green },
  del: { bgcolor: '#FDF2F2', color: CK.red },
  ctx: { color: CK.muted },
};
const prefix = { add: '+', del: '−', ctx: ' ' };

// TemplateDiffView renders a legible unified line diff of the deployed template
// vs the edited one — the StackSet stand-in for a CloudFormation change set
// (StackSets have no native change-set API). Collapsed by default.
export default function TemplateDiffView({ oldText, newText }) {
  const [open, setOpen] = useState(false);
  const diff = useMemo(() => computeLineDiff(oldText, newText), [oldText, newText]);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ ...insetPanelSx, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem', minWidth: 0 }}>
          Template changes
        </Typography>
        <Chip size="small" label={`+${diff.added} added`} sx={statusChipSx('success')} />
        <Chip size="small" label={`−${diff.removed} removed`} sx={statusChipSx('error')} />
        {!diff.tooLarge && diff.rows.length > 0 && (
          <Button size="small" onClick={() => setOpen((o) => !o)} sx={{ fontFamily: FONT_BODY }}>
            {open ? 'Hide diff' : 'Show diff'}
          </Button>
        )}
      </Box>
      {diff.tooLarge && (
        <Typography sx={{ ...emptyTextSx, fontSize: '0.75rem', mt: 1, display: 'block' }}>
          Template is large — showing a summary only ({diff.added} lines added, {diff.removed} removed).
        </Typography>
      )}
      {!diff.tooLarge && (
        <Collapse in={open}>
          <Box
            sx={{
              mt: 1, fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.5,
              border: `1px solid ${CK.border}`, borderRadius: '8px',
              overflow: 'auto', maxHeight: 380,
            }}
          >
            {diff.rows.map((r, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={idx} sx={{ px: 1, whiteSpace: 'pre', ...diffRowSx[r.type] }}>
                {`${prefix[r.type]} ${r.text}`}
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
